import { ReactElement, useEffect, useRef, useState } from 'react';
import SearchInput from './SearchInput';
import Button from './Button';
import Tabs from './Tabs';

type FilterOptionsProps = {
  toggleVisibility: (inp: boolean) => void;
  position: 'left' | 'right';
};

const FILTER_TABS = [
  { id: 'Tabs', name: 'Tabs' },
  { id: 'Configuration', name: 'Configuration' },
  { id: 'Connection', name: 'Connection' },
  { id: 'Tags', name: 'Tags' }
];

const options = {
  V2Duet: true,
  Lab1: false,
  Lab2: false,
  V2Duet1: true,
  Lab3: false,
  Lab4: false
};

/**
 * Renders a Filter component to display filtering options.
 * @param props - Properties of FilterOptions.
 * @returns - A React component for rendering filter options.
 */
function FilterOptions(props: FilterOptionsProps): ReactElement {
  const { toggleVisibility, position } = props;
  const [selectedTab, setSelectedTab] = useState('Tabs');
  const [searchText, setSearchText] = useState('');
  const [checkboxes, setCheckboxes] = useState(options);

  /**
   * Handles changes in checkbox input values.
   * @param event - The event object representing the checkbox input change.
   */
  const handleCheckboxChange = (event: any): void => {
    const { name, checked } = event.target;
    setCheckboxes({ ...checkboxes, [name]: checked });
  };

  /**
   * Handles the form submission.
   */
  const handleSubmit = (): void => {
    // changing the visibility of filter options.
    toggleVisibility(false);
  };

  return (
    <div
      className={`absolute ${
        position === 'left' ? 'left-0' : 'right-0'
      }  z-30 p-4 mt-2 origin-top-right rounded-lg bg-white dark:bg-container-700 border border-gray-200 dark:border-gray-700 shadow-md`}
    >
      <div className="pb-5">
        <Tabs
          selectedTab={selectedTab}
          onTabClick={setSelectedTab}
          tabs={FILTER_TABS}
          tabType="full-width"
          selectedTabBackground="light"
        />
      </div>
      <SearchInput textValue={searchText} setText={setSearchText} />
      <div className="py-5 text-gray-500 text-xs font-normal">
        <form className="grid grid-cols-2">
          {Object.entries(checkboxes).map(([currentOption, checked]) => (
            <div key={currentOption} className="py-2">
              <label htmlFor={currentOption} className="flex">
                <input
                  id={currentOption}
                  type="checkbox"
                  name={currentOption}
                  className="appearance-none h-4 w-4 rounded border border-gray-500 mr-2 checked:border-primary-500 text-primary-500"
                  checked={checked}
                  onChange={handleCheckboxChange}
                />
                {checked && (
                  <span
                    className="material-icons-outlined text-sm  absolute 
      w-4 h-4 mt-[-0.11875rem] ml-[0.075rem] text-primary-500
       peer-checked:block"
                  >
                    done
                  </span>
                )}
                {currentOption}
              </label>
            </div>
          ))}
        </form>
      </div>
      <Button theme="primary" size="sm" name="Apply Filters" onClick={handleSubmit} />
    </div>
  );
}

type FilterProps = {
  position: 'left' | 'right';
  label?: string;
};

/**
 * Renders the Options so user can filter out devices or Groups A/C To that.
 * @param props - Properties of Filter.
 * @returns - A react Component for rendering Filter Options.
 */
export default function Filter(props: FilterProps): ReactElement {
  const { position, label } = props;
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  /**
   * HandelOutsideClick Function Handles clicks outside the FilterOptions to close it.
   * @param  event - The click event.
   */
  const handleOutsideClick = (event: MouseEvent): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOptionsVisible(false);
    }
  };

  /**
   * A changeVisibility Function for changing the visibility of the FilterOption Component.
   */
  const changeVisibility = (): void => {
    setIsOptionsVisible(!isOptionsVisible);
  };

  useEffect(() => {
    if (isOptionsVisible) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOptionsVisible]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        className="inline-flex w-full justify-center items-center gap-x-1.5 rounded border border-gray-200 dark:border-gray-700 shadow-xs bg-white dark:bg-container-500 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-400  hover:bg-gray-200 dark:hover:bg-container-600"
        onClick={changeVisibility}
      >
        {label || 'Filters'}
        <span className="material-icons-outlined text-gray-700 dark:text-gray-400">
          {!isOptionsVisible ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
        </span>
      </button>
      {isOptionsVisible && (
        <FilterOptions position={position} toggleVisibility={setIsOptionsVisible} />
      )}
    </div>
  );
}
